import { ActivityV2 } from 'models/activityV2'
import { EventIcon } from '../EventIcon'
import { formatEventTime } from '../utils'
import { ReactNode } from 'react'

interface BaseEventCardProps {
  event: ActivityV2
  children: ReactNode
}

/**
 * Base layout component for all event cards.
 * Provides consistent structure: icon, content area, and timestamp.
 */
export function BaseEventCard({ event, children }: BaseEventCardProps) {
  const time = formatEventTime(event.created_at)

  return (
    <div className="flex items-start gap-3">
      {/* Circular icon — sits on the vertical timeline connector.
          Bright green palette: mint background + vibrant green ring. */}
      <div
        className="flex-shrink-0 w-9 h-9 rounded-full bg-green-50 flex items-center justify-center relative z-[2]"
        style={{ border: '1px solid rgba(21, 128, 61, 0.5)' }}
      >
        <EventIcon
          contentType={event.content_type}
          actionType={event.action_type}
          userName={event.user_name}
        />
      </div>

      {/* Card content — white card nested inside the day's gray container */}
      <div className="flex-1 min-w-0 bg-white rounded-lg px-3 py-3 shadow-sm">
        <div className="flex items-start justify-between gap-2">
          <div className="flex-1 min-w-0">{children}</div>

          {/* Timestamp */}
          <div className="flex-shrink-0">
            <span className="font-inter text-base leading-[1.43] text-right text-gray-500 tabular-nums">
              {time}
            </span>
          </div>
        </div>
      </div>
    </div>
  )
}

/**
 * Action verb mapping for different object types and actions.
 */
const ACTION_VERBS: Record<string, Record<string, string>> = {
  CREATE: {
    note: 'added',
    reminder: 'scheduled',
    'sample request': 'submitted',
    default: 'created',
  },
  UPDATE: {
    default: 'updated',
  },
  DELETE: {
    default: 'deleted',
  },
}

/**
 * Generates action text with consistent styling for object types.
 */
export function getActionText(
  actionType: string,
  objectType: string
): ReactNode {
  const objectSpan = (
    <span className="text-gray-700 font-medium">{objectType}</span>
  )

  const verbs = ACTION_VERBS[actionType] || ACTION_VERBS.UPDATE
  const verb = verbs[objectType] || verbs.default || 'modified'

  return (
    <>
      {verb} a {objectSpan}
    </>
  )
}
